import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  School as CourseIcon,
  CheckCircle as CompletedIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  EmojiEvents as BadgeIcon,
  CardMembership as CertificateIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { analyticsService, UserStats, TrendData } from '../../services/analyticsService';
import StatsCard from './StatsCard';
import TrendChart from './TrendChart';
import CoursePerformance from './CoursePerformance';

type Period = '7' | '30' | '90';

const AnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [trends, setTrends] = useState<TrendData | null>(null);
  const [period, setPeriod] = useState<Period>('30');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, trendsData] = await Promise.all([
        analyticsService.getMyStats(),
        analyticsService.getTrends(parseInt(period)),
      ]);
      setStats(statsData);
      setTrends(trendsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(t('analytics.errorLoading', 'Fehler beim Laden der Analytics'));
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: Period | null) => {
    if (newPeriod) {
      setPeriod(newPeriod);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds) return '0h';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatTimeMinutes = (value: number) => {
    if (value < 60) return `${value}m`;
    return `${Math.floor(value / 60)}h ${value % 60}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return null;
  }

  const lessonCompletionsData = trends
    ? analyticsService.fillMissingDates(trends.lessonCompletions, parseInt(period), 'count')
    : [];

  const timeByDayData = trends
    ? analyticsService.fillMissingDates(
        trends.timeByDay.map(d => ({ date: d.date, value: Math.round(d.timeSpent / 60) })),
        parseInt(period),
        'value'
      )
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t('analytics.title', 'Meine Lernstatistiken')}
        </Typography>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          size="small"
        >
          <ToggleButton value="7">
            {t('analytics.period.week', '7 Tage')}
          </ToggleButton>
          <ToggleButton value="30">
            {t('analytics.period.month', '30 Tage')}
          </ToggleButton>
          <ToggleButton value="90">
            {t('analytics.period.quarter', '90 Tage')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.totalCourses', 'Kurse gesamt')}
            value={stats.summary.totalCourses}
            icon={<CourseIcon />}
            color="primary"
            subtitle={`${stats.summary.inProgressCourses} ${t('analytics.inProgress', 'in Bearbeitung')}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.completedCourses', 'Abgeschlossen')}
            value={stats.summary.completedCourses}
            icon={<CompletedIcon />}
            color="success"
            subtitle={`${Math.round((stats.summary.completedCourses / (stats.summary.totalCourses || 1)) * 100)}% ${t('analytics.completionRate', 'Abschlussrate')}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.learningTime', 'Lernzeit')}
            value={formatTime(stats.summary.totalTimeSpent)}
            icon={<TimeIcon />}
            color="info"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.achievements', 'Erfolge')}
            value={stats.summary.certificatesEarned + stats.summary.badgesEarned}
            icon={<BadgeIcon />}
            color="warning"
            subtitle={`${stats.summary.certificatesEarned} Zert. / ${stats.summary.badgesEarned} Auszeichn.`}
          />
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label={t('analytics.tabs.overview', 'Übersicht')} />
          <Tab label={t('analytics.tabs.courses', 'Kurse')} />
          <Tab label={t('analytics.tabs.activity', 'Aktivität')} />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TrendChart
              title={t('analytics.lessonsCompleted', 'Abgeschlossene Lektionen')}
              data={lessonCompletionsData}
              type="bar"
              color="#4caf50"
              height={250}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TrendChart
              title={t('analytics.learningTimePerDay', 'Lernzeit pro Tag')}
              data={timeByDayData}
              type="area"
              color="#2196f3"
              height={250}
              formatValue={(v) => formatTimeMinutes(v)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.recentCompletions', 'Letzte Abschlüsse')}
                </Typography>
                {stats.recentCompletions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {t('analytics.noCompletions', 'Noch keine Abschlüsse')}
                  </Typography>
                ) : (
                  <List dense>
                    {stats.recentCompletions.slice(0, 5).map((completion, index) => (
                      <ListItem key={index} divider={index < stats.recentCompletions.length - 1}>
                        <ListItemText
                          primary={completion.lessonTitle}
                          secondary={completion.courseTitle}
                        />
                        {completion.score !== null && (
                          <Chip
                            label={`${completion.score}%`}
                            size="small"
                            color={completion.score >= 80 ? 'success' : completion.score >= 50 ? 'primary' : 'warning'}
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.progressSummary', 'Fortschrittsübersicht')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('analytics.avgProgress', 'Durchschnittlicher Fortschritt')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {Math.round(stats.summary.avgProgress || 0)}%
                    </Typography>
                  </Box>
                  {stats.summary.avgScore !== null && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('analytics.avgScore', 'Durchschnittliche Punktzahl')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {Math.round(stats.summary.avgScore)}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Courses Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.myCourses', 'Meine Kurse')}
                </Typography>
                {stats.courses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {t('analytics.noCourses', 'Keine Kurse vorhanden')}
                  </Typography>
                ) : (
                  <List>
                    {stats.courses.map((course) => (
                      <ListItem key={course.id} divider>
                        <ListItemText
                          primary={course.courseTitle}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {t('analytics.enrolled', 'Eingeschrieben')}: {new Date(course.enrolledAt).toLocaleDateString('de-DE')}
                              </Typography>
                              {course.timeSpentSeconds > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('analytics.time', 'Zeit')}: {formatTime(course.timeSpentSeconds)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            label={
                              course.status === 'completed'
                                ? t('analytics.status.completed', 'Abgeschlossen')
                                : course.status === 'in_progress'
                                ? t('analytics.status.inProgress', 'In Bearbeitung')
                                : t('analytics.status.enrolled', 'Eingeschrieben')
                            }
                            color={
                              course.status === 'completed'
                                ? 'success'
                                : course.status === 'in_progress'
                                ? 'primary'
                                : 'default'
                            }
                            size="small"
                          />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 50, textAlign: 'right' }}>
                            {course.progressPercentage}%
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Activity Tab */}
      {tabValue === 2 && trends && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <TrendChart
              title={t('analytics.activityOverview', 'Aktivitätsübersicht')}
              data={lessonCompletionsData}
              type="line"
              height={350}
              multipleLines={[
                {
                  dataKey: 'value',
                  color: '#4caf50',
                  name: t('analytics.lessonsCompleted', 'Abgeschlossene Lektionen'),
                },
              ]}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.enrollmentTrend', 'Einschreibungen')}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {trends.enrollments.reduce((sum, e) => sum + e.count, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('analytics.inLast', 'In den letzten')} {period} {t('analytics.days', 'Tagen')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('analytics.completionTrend', 'Kursabschlüsse')}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {trends.completions.reduce((sum, c) => sum + c.count, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('analytics.inLast', 'In den letzten')} {period} {t('analytics.days', 'Tagen')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AnalyticsDashboard;
